import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import CustomToast from "../../components/misc/CustomToast";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/misc/Card";
import Spinner from "../../components/misc/Spinner";
import { useIsMobile } from "../../hooks/useIsMobile";
import { cn } from "../../utils/classMerger";
import AppConfigGroupCard from "./AppConfigGroupCard";
import { getAppConfigs } from "./actions";
import { AppConfigItem } from "./types";

// Groups whose keys must change together (post_retry.max_retries is the backoff
// list's length) or need a custom control keep their own screen.
const DEDICATED_SCREENS: Record<string, { name: string; path: string }> = {
    POST_RETRY: { name: "Failed Posts", path: "/admin/failed-posts" },
    WEEKLY_SUMMARY: { name: "Weekly Summary", path: "/admin/weekly-summary" },
};

const groupItems = (items: AppConfigItem[]): AppConfigItem[][] => {
    const groups = new Map<string, AppConfigItem[]>();
    for (const item of items) groups.set(item.group, [...(groups.get(item.group) ?? []), item]);
    return Array.from(groups.values());
};

const AppConfig: React.FC = () => {
    const mobile = useIsMobile(900);
    const [items, setItems] = useState<AppConfigItem[] | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const data = await getAppConfigs();
                if (!cancelled) setItems(data);
            } catch {
                if (!cancelled) {
                    setFailed(true);
                    toast.error(<CustomToast message="Failed to load app config" type="error" />, { autoClose: 3000 });
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    const groups = items ? groupItems(items) : [];

    return (
        <div className={mobile ? "p-4" : "p-6"}>
            <div className="grid items-start gap-8">
                <div className="grid gap-1 px-2">
                    <h1 className={cn("font-bold tracking-tight text-primary", mobile ? "text-2xl" : "text-3xl")}>
                        App Config
                    </h1>
                    <p className="text-gray-500">
                        Settings the app reads while it runs. A change applies from the next run, with no deploy.
                    </p>
                </div>
                {!items && !failed && (
                    <div className="py-6">
                        <Spinner loadingMessage="Loading app config..." />
                    </div>
                )}
                {failed && (
                    <Card>
                        <CardContent className="py-6 text-sm text-gray-500">
                            Could not load the settings. Reload the page to try again.
                        </CardContent>
                    </Card>
                )}
                {items && groups.length === 0 && (
                    <Card>
                        <CardContent className="py-6 text-sm text-gray-500">
                            No settings are seeded in this environment yet. Run the app-config seed and reload.
                        </CardContent>
                    </Card>
                )}
                {groups.map((group) => {
                    const first = group[0];
                    const screen = DEDICATED_SCREENS[first.group];
                    if (!screen) return <AppConfigGroupCard key={first.group} items={group} />;
                    return (
                        <Card key={first.group}>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-left">{first.groupLabel || first.group}</CardTitle>
                                {first.groupDescription && (
                                    <p className="text-sm text-gray-500">{first.groupDescription}</p>
                                )}
                            </CardHeader>
                            <CardContent className="pt-0 text-sm text-gray-500">
                                These settings are edited on their own screen:{" "}
                                <Link
                                    to={screen.path}
                                    className="font-semibold text-primary underline-offset-4 hover:underline"
                                >
                                    {screen.name}
                                </Link>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        </div>
    );
};

export default AppConfig;
